import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const baseURL =
  process.env.NODE_ENV === 'production'
    ? 'https://api.metaf.global/'
    : 'https://api.metafastest.info/';
const axiosInstance = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});
const Divider = () => (
  <div
    style={{
      width: '100%',
      height: '1px',
      backgroundColor: '#e0e0e0',
      margin: '20px 0',
    }}
  ></div>
);
const {
  default: SaleSubmissonFormStyleWrapper,
} = require('./SaleSubmissonForm.style');

const SaleSubmissonForm = () => {
  const { t } = useTranslation();
  // Remove preview, not needed for multi-upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({
    // Buyer
    buyerIdentifier: '',
    buyerName: '',
    buyerEmail: '',
    buyerContact: '',
    buyerCardDeliveryAddress: '',
    // Requester
    requesterIdentifier: '',
    requesterName: '',
    requesterContact: '',
    requesterEmail: '',
    // Referrer
    referrerIdentifier: '',
    referrerName: '',
    referrerEmail: '',
    referrerContact: '',
    referrerCardDeliveryAddress: '',
    // Sale
    amount: '',
    // Receipts
    receiptUrls: [],
  });
  // const [sameAsBuyer, setSameAsBuyer] = useState(false);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setValues((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: '' })); // clear error on typing
  };

  // Handler for same as buyer checkbox
  // const handleSameAsBuyerChange = (e) => {
  //   const checked = e.target.checked;
  //   setSameAsBuyer(checked);
  //   if (checked) {
  //     setValues((prev) => ({
  //       ...prev,
  //       requesterIdentifier: prev.buyerIdentifier,
  //       requesterName: prev.buyerName,
  //       requesterContact: prev.buyerContact,
  //       requesterEmail: prev.buyerEmailAddress,
  //     }));
  //   } else {
  //     setValues((prev) => ({
  //       ...prev,
  //       requesterIdentifier: '',
  //       requesterName: '',
  //       requesterContact: '',
  //       requesterEmail: '',
  //     }));
  //   }
  // };

  const validateForm = () => {
    const newErrors = {};

    // Buyer required fields
    if (!values.buyerIdentifier)
      newErrors.buyerIdentifier = t(
        'saleSubmission.errors.buyerIdentifierRequired'
      );
    if (!values.buyerName)
      newErrors.buyerName = t('saleSubmission.errors.buyerNameRequired');
    if (!values.buyerEmail) {
      newErrors.buyerEmail = t('saleSubmission.errors.buyerEmailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.buyerEmail)) {
      newErrors.buyerEmail = t('saleSubmission.errors.emailInvalid');
    }
    if (!values.buyerContact)
      newErrors.buyerContact = t('saleSubmission.errors.buyerContactRequired');
    if (!values.buyerCardDeliveryAddress)
      newErrors.buyerCardDeliveryAddress = t(
        'saleSubmission.errors.buyerCardDeliveryAddressRequired'
      );

    // Requester required fields
    // if (!values.requesterIdentifier)
    //   newErrors.requesterIdentifier = t(
    //     'saleSubmission.errors.requesterIdentifierRequired'
    //   );
    // if (!values.requesterName)
    //   newErrors.requesterName = t(
    //     'saleSubmission.errors.requesterNameRequired'
    //   );
    // if (!values.requesterContact)
    //   newErrors.requesterContact = t(
    //     'saleSubmission.errors.requesterContactRequired'
    //   );
    // if (!values.requesterEmail) {
    //   newErrors.requesterEmail = t('saleSubmission.errors.emailRequired');
    // } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.requesterEmail)) {
    //   newErrors.requesterEmail = t('saleSubmission.errors.emailInvalid');
    // }

    // Referrer fields validation - if any referrer field is filled, all are required
    const referrerFieldsFilled =
      values.referrerIdentifier.trim() !== '' ||
      values.referrerName.trim() !== '' ||
      values.referrerEmail.trim() !== '' ||
      values.referrerContact.trim() !== '' ||
      values.referrerCardDeliveryAddress.trim() !== '';

    if (referrerFieldsFilled) {
      if (!values.referrerIdentifier)
        newErrors.referrerIdentifier = t(
          'saleSubmission.errors.referrerIdentifierRequired'
        );
      if (!values.referrerName)
        newErrors.referrerName = t(
          'saleSubmission.errors.referrerNameRequired'
        );
      if (!values.referrerEmail) {
        newErrors.referrerEmail = t('saleSubmission.errors.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.referrerEmail)) {
        newErrors.referrerEmail = t('saleSubmission.errors.emailInvalid');
      }
      if (!values.referrerContact)
        newErrors.referrerContact = t(
          'saleSubmission.errors.referrerContactRequired'
        );
      if (!values.referrerCardDeliveryAddress)
        newErrors.referrerCardDeliveryAddress = t(
          'saleSubmission.errors.referrerCardDeliveryAddressRequired'
        );
    }

    if (!values.amount) {
      newErrors.amount = t('saleSubmission.errors.amountRequired');
    } else if (isNaN(Number(values.amount)) || Number(values.amount) <= 0) {
      newErrors.amount = t('saleSubmission.errors.amountInvalid');
    }

    // Receipt validation: at least 1, at most 5
    if (!values.receiptUrls || values.receiptUrls.length === 0) {
      newErrors.receiptUrls = t('saleSubmission.errors.receiptRequired');
    } else if (values.receiptUrls.length > 5) {
      newErrors.receiptUrls =
        t('saleSubmission.errors.receiptTooMany') || 'Maximum 5 images allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // New: handle multiple file selection
  const onSelectImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Limit: total (already uploaded + new) <= 5
    if ((values.receiptUrls?.length || 0) + files.length > 5) {
      alert('Maximum 5 images allowed');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      // Upload all files in parallel (could do sequential if needed)
      const uploadPromises = files.map((file) => uploadFile(file));
      await Promise.all(uploadPromises);
      setErrors((prev) => ({ ...prev, receiptUrls: '' }));
    } catch {
      alert('File upload failed, please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Clear the input value so user can upload same file again if needed
      e.target.value = '';
    }
  };

  // Upload a file and add its URL to receiptUrls
  const uploadFile = async (file) => {
    if (!file || file.size / 1024 > 10000) {
      alert('File size should be less than 10MB');
      return;
    }
    const formData = new FormData();
    formData.append('files', file);
    try {
      const { data } = await axiosInstance.post(
        'token-purchase-request/upload',
        formData,
        {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percent);
            }
          },
        }
      );
      if (data) {
        setValues((prev) => {
          let urls = Array.isArray(prev.receiptUrls)
            ? [...prev.receiptUrls]
            : [];
          // Only add if not exceeding 5
          if (urls.length < 5) {
            urls.push(data[0].url);
          }
          if (urls.length > 5) urls = urls.slice(0, 5);
          return { ...prev, receiptUrls: urls };
        });
      }
    } catch {
      throw new Error('Upload failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      // Buyer
      buyerIdentifier: String(values.buyerIdentifier),
      buyerName: String(values.buyerName),
      buyerEmail: String(values.buyerEmail),
      buyerContact: String(values.buyerContact),
      buyerCardDeliveryAddress: String(values.buyerCardDeliveryAddress),
      // Requester
      // requesterIdentifier: String(values.requesterIdentifier),
      // requesterName: String(values.requesterName),
      // requesterContact: String(values.requesterContact),
      // requesterEmail: String(values.requesterEmail),
      // Referrer
      referrerIdentifier: String(values.referrerIdentifier || ''),
      referrerName: String(values.referrerName || ''),
      referrerEmail: String(values.referrerEmail || ''),
      referrerContact: String(values.referrerContact || ''),
      referrerCardDeliveryAddress: String(
        values.referrerCardDeliveryAddress || ''
      ),
      // Sale
      amount: Number(values.amount),
      // Receipts
      receiptUrls: Array.isArray(values.receiptUrls)
        ? values.receiptUrls.slice(0, 5)
        : [],
    };

    // Remove keys whose value is '', null, or []
    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === '' ||
        payload[key] === null ||
        (Array.isArray(payload[key]) && payload[key].length === 0)
      ) {
        delete payload[key];
      }
    });

    try {
      setSubmitting(true);
      const { data } = await axiosInstance.post(
        'token-purchase-request/register',
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      alert(data?.message || 'Submitted successfully!');
      setValues({
        buyerIdentifier: '',
        buyerName: '',
        buyerEmail: '',
        buyerContact: '',
        buyerCardDeliveryAddress: '',
        requesterIdentifier: '',
        requesterName: '',
        requesterContact: '',
        requesterEmail: '',
        referrerIdentifier: '',
        referrerName: '',
        referrerEmail: '',
        referrerContact: '',
        referrerCardDeliveryAddress: '',
        amount: '',
        receiptUrls: [],
      });
    } catch (error) {
      alert(
        error?.response?.data?.message || 'Submission failed, please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Remove an image by index
  const handleRemoveImage = (index) => {
    setValues((prev) => ({
      ...prev,
      receiptUrls: prev.receiptUrls.filter((_, i) => i !== index),
    }));
  };

  return (
    <SaleSubmissonFormStyleWrapper>
      <div className="sale_submisson_form">
        <h3 className="from_title">{t('saleSubmission.title')}</h3>
        <div className="sale_submisson_user_form">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* ----------- Buyer Section ----------- */}
              <div className="form-group col-md-6">
                <label htmlFor="buyerIdentifier">
                  {t('saleSubmission.buyerIdentifier')}
                  <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  id="buyerIdentifier"
                  placeholder={t('saleSubmission.placeholderBuyerId')}
                  className="form-control"
                  value={values.buyerIdentifier}
                  onChange={handleInputChange}
                />
                {errors.buyerIdentifier && (
                  <div className="form-error">{errors.buyerIdentifier}</div>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="buyerName">
                  {t('saleSubmission.buyerName')}
                  <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  id="buyerName"
                  placeholder={t('saleSubmission.placeholderBuyerName')}
                  className="form-control"
                  value={values.buyerName}
                  onChange={handleInputChange}
                />
                {errors.buyerName && (
                  <div className="form-error">{errors.buyerName}</div>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="buyerEmail">
                  {t('saleSubmission.buyerEmailAddress')}
                  <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  id="buyerEmail"
                  placeholder={t('saleSubmission.buyerEmailAddress')}
                  className="form-control"
                  value={values.buyerEmail}
                  onChange={handleInputChange}
                />
                {errors.buyerEmail && (
                  <div className="form-error">{errors.buyerEmail}</div>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="buyerContact">
                  {t('saleSubmission.buyerContact')}
                  <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  id="buyerContact"
                  placeholder="e.g. +84 1234 56789"
                  className="form-control"
                  value={values.buyerContact}
                  onChange={handleInputChange}
                />
                {errors.buyerContact && (
                  <div className="form-error">{errors.buyerContact}</div>
                )}
              </div>
              {/* ----------- Amount ----------- */}
              <div className="form-group col-md-12">
                <label htmlFor="amount">
                  {t('saleSubmission.amount')}
                  <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  id="amount"
                  placeholder={t('saleSubmission.placeholderAmount')}
                  className="form-control"
                  value={values.amount}
                  onChange={handleInputChange}
                />
                {errors.amount && (
                  <div className="form-error">{errors.amount}</div>
                )}
              </div>
              <div className="form-group col-md-12">
                <label htmlFor="buyerCardDeliveryAddress">
                  {t('saleSubmission.buyerCardDeliveryAddress')}
                  <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  id="buyerCardDeliveryAddress"
                  placeholder={t(
                    'saleSubmission.placeholderBuyerCardDeliveryAddress'
                  )}
                  className="form-control"
                  value={values.buyerCardDeliveryAddress}
                  onChange={handleInputChange}
                />
                {errors.buyerCardDeliveryAddress && (
                  <div className="form-error">
                    {errors.buyerCardDeliveryAddress}
                  </div>
                )}
              </div>
              <Divider />
              {/* ----------- Requester Section ----------- */}
              {/*
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  marginLeft: 15,
                }}
              >
                <label htmlFor="sameAsBuyer" className="form-check">
                  <input
                    type="checkbox"
                    id="sameAsBuyer"
                    checked={sameAsBuyer}
                    style={{ margin: 0, width: 16, height: 16 }}
                    onChange={handleSameAsBuyerChange}
                  />
                  <span className="form-check-label">
                    {t('saleSubmission.sameAsBuyerLabel')}
                  </span>
                </label>
              </div>

              <div className="form-group col-md-6">
                <label htmlFor="requesterIdentifier">
                  {t('saleSubmission.requesterIdentifier')}
                </label>
                <input
                  type="text"
                  id="requesterIdentifier"
                  placeholder={t('saleSubmission.placeholderRequesterId')}
                  className="form-control"
                  value={values.requesterIdentifier}
                  onChange={handleInputChange}
                  disabled={sameAsBuyer}
                />
                {errors.requesterIdentifier && (
                  <div className="form-error">{errors.requesterIdentifier}</div>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="requesterName">
                  {t('saleSubmission.requesterName')}
                </label>
                <input
                  type="text"
                  id="requesterName"
                  placeholder={t('saleSubmission.placeholderRequesterName')}
                  className="form-control"
                  value={values.requesterName}
                  onChange={handleInputChange}
                  disabled={sameAsBuyer}
                />
                {errors.requesterName && (
                  <div className="form-error">{errors.requesterName}</div>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="requesterContact">
                  {t('saleSubmission.requesterContact')}
                </label>
                <input
                  type="text"
                  id="requesterContact"
                  placeholder="e.g. +84 2586 25866"
                  className="form-control"
                  value={values.requesterContact}
                  onChange={handleInputChange}
                  disabled={sameAsBuyer}
                />
                {errors.requesterContact && (
                  <div className="form-error">{errors.requesterContact}</div>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="requesterEmail">
                  {t('saleSubmission.requesterEmail')}
                </label>
                <input
                  type="text"
                  id="requesterEmail"
                  placeholder="e.g. example@gmail.com"
                  className="form-control"
                  value={values.requesterEmail}
                  onChange={handleInputChange}
                  disabled={sameAsBuyer}
                />
                {errors.requesterEmail && (
                  <div className="form-error">{errors.requesterEmail}</div>
                )}
              </div>
              <Divider />
              */}
              {/* ----------- Referrer Section ----------- */}
              <div className="form-group col-md-6">
                <label htmlFor="referrerIdentifier">
                  {t('saleSubmission.referrerIdentifier')}
                </label>
                <input
                  type="text"
                  id="referrerIdentifier"
                  placeholder={t('saleSubmission.placeholderReferrerId')}
                  className="form-control"
                  value={values.referrerIdentifier}
                  onChange={handleInputChange}
                />
                {errors.referrerIdentifier && (
                  <div className="form-error">{errors.referrerIdentifier}</div>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="referrerName">
                  {t('saleSubmission.referrerName')}
                </label>
                <input
                  type="text"
                  id="referrerName"
                  placeholder={t('saleSubmission.placeholderReferrerName')}
                  className="form-control"
                  value={values.referrerName}
                  onChange={handleInputChange}
                />
                {errors.referrerName && (
                  <div className="form-error">{errors.referrerName}</div>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="referrerEmail">
                  {t('saleSubmission.referrerEmail')}
                </label>
                <input
                  type="text"
                  id="referrerEmail"
                  placeholder="e.g. referrer@email.com"
                  className="form-control"
                  value={values.referrerEmail}
                  onChange={handleInputChange}
                />
                {errors.referrerEmail && (
                  <div className="form-error">{errors.referrerEmail}</div>
                )}
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="referrerContact">
                  {t('saleSubmission.referrerContact')}
                </label>
                <input
                  type="text"
                  id="referrerContact"
                  placeholder="e.g. +84 1234 56789"
                  className="form-control"
                  value={values.referrerContact}
                  onChange={handleInputChange}
                />
                {errors.referrerContact && (
                  <div className="form-error">{errors.referrerContact}</div>
                )}
              </div>
              <div className="form-group col-md-12">
                <label htmlFor="referrerCardDeliveryAddress">
                  {t('saleSubmission.referrerCardDeliveryAddress')}
                </label>
                <input
                  type="text"
                  id="referrerCardDeliveryAddress"
                  placeholder={t(
                    'saleSubmission.placeholderReferrerCardDeliveryAddress'
                  )}
                  className="form-control"
                  value={values.referrerCardDeliveryAddress}
                  onChange={handleInputChange}
                />
                {errors.referrerCardDeliveryAddress && (
                  <div className="form-error">
                    {errors.referrerCardDeliveryAddress}
                  </div>
                )}
              </div>

              {/* ----------- Receipt Upload ----------- */}
              <div className="form-group col-md-12">
                <label className="form-label">
                  {t('saleSubmission.paymentReceipt')}
                  <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={
                      uploading ||
                      (values.receiptUrls && values.receiptUrls.length >= 5)
                    }
                    onChange={onSelectImages}
                  />
                </div>
                {/* Previews of uploaded images */}
                {values.receiptUrls && values.receiptUrls.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    {values.receiptUrls.map((url, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: 90,
                          height: 90,
                          border: '1px solid #ccc',
                          borderRadius: 6,
                          overflow: 'hidden',
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#fafbfc',
                        }}
                      >
                        {/* Delete button */}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          style={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            width: 18,
                            height: 18,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            fontSize: 12,
                            lineHeight: '18px',
                            textAlign: 'center',
                          }}
                          aria-label="Remove"
                        >
                          ✕
                        </button>
                        <img
                          src={url}
                          alt={`Receipt ${idx + 1}`}
                          style={{
                            width: 88,
                            height: 88,
                            objectFit: 'cover',
                            borderRadius: 5,
                          }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            bottom: 2,
                            right: 4,
                            fontSize: 11,
                            color: '#888',
                            background: '#fff8',
                            borderRadius: 3,
                            padding: '0 3px',
                          }}
                        >
                          {idx + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Upload progress bar */}
                {uploading && (
                  <div style={{ width: '100%', marginTop: 8 }}>
                    <div
                      style={{
                        width: `${uploadProgress}%`,
                        height: 6,
                        backgroundColor: '#007bff',
                        borderRadius: 3,
                        transition: 'width 0.3s ease',
                      }}
                    ></div>
                    <small>{uploadProgress}%</small>
                  </div>
                )}
                {errors.receiptUrls && (
                  <div className="form-error">{errors.receiptUrls}</div>
                )}
              </div>
            </div>

            <div
              className="form-group col-md-12 text-center"
              style={{ marginTop: 16 }}
            >
              <button
                type="submit"
                className="btn btn-primary"
                disabled={uploading || submitting}
              >
                {submitting
                  ? t('saleSubmission.submittingButton')
                  : t('saleSubmission.submitButton')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </SaleSubmissonFormStyleWrapper>
  );
};

export default SaleSubmissonForm;
